import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronBack } from 'react-icons/io5';
import QRCode from 'qrcode';
import InfoTooltip from '../../../components/common/InfoTooltip';
import { useAuth } from '../../../context/AuthContext';

// ───── 스타일 ─────

const Page = styled.div`
  margin: -${({ theme }) => theme.realtor.pagePadding};
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.borderLight};
  display: flex;
  flex-direction: column;
`;

/* 헤더 — AppHeader 스타일 */
const Header = styled.div`
  display: flex;
  align-items: center;
  height: 52px;
  padding: 0 ${({ theme }) => theme.spacing.base} 0 ${({ theme }) => theme.spacing.sm};
  background: #fff;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.xs};
  min-width: 32px;
  min-height: 44px;
  background: none;
  border: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
`;

const HeaderTitle = styled.span`
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-left: ${({ theme }) => theme.spacing.xs};
`;

/* 메인 카드 */
const Card = styled.div`
  margin: ${({ theme }) => theme.spacing.base};
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const Desc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  line-height: 1.5;
  margin: 0 0 ${({ theme }) => theme.spacing.xl};
`;

const QrArea = styled.div`
  align-self: center;
  width: 220px;
  height: 220px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
`;

const QrImg = styled.img`
  width: 180px;
  height: 180px;
`;

const QrUrl = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textLight};
  text-align: center;
  word-break: break-all;
  margin: 0 0 ${({ theme }) => theme.spacing.xl};
  line-height: 1.4;
`;

const BtnRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const OutlineBtn = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md} 0;
  background: #fff;
  color: ${({ theme }) => theme.colors.primary};
  border: 1.5px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;

  &:hover { opacity: 0.85; }
`;

const FilledBtn = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md} 0;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: 1.5px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  text-align: center;
  line-height: 1.3;

  &:hover { opacity: 0.85; }
`;

// ───── 컴포넌트 ─────
const QrCode = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const uid = userData?.uid || 'demo';
  const formUrl = `https://ieum-landing.web.app/apply?ref=${uid}`;
  const [qrDataUrl, setQrDataUrl] = useState('');

  useEffect(() => {
    QRCode.toDataURL(formUrl, { width: 360, margin: 2 })
      .then(setQrDataUrl)
      .catch(() => {});
  }, [formUrl]);

  const handleShare = () => {
    // RN 앱이면 네이티브 공유 시트
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        command: 'share',
        text: '이음 전용 신청 폼',
        url: formUrl,
      }));
      return;
    }
    if (navigator.share) {
      navigator.share({ title: '이음 전용 신청 폼', url: formUrl });
    } else {
      navigator.clipboard.writeText(formUrl);
      alert('링크가 복사되었습니다.');
    }
  };

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const link = document.createElement('a');
    link.download = `ieum-qr-${uid}.png`;
    link.href = qrDataUrl;
    link.click();
  };

  return (
    <Page>
      <Header>
        <BackBtn onClick={() => navigate(-1)}>
          <IoChevronBack size={22} />
        </BackBtn>
        <HeaderTitle>내 QR 코드</HeaderTitle>
      </Header>

      <Card>
        <Title>전용 QR코드 (고객 폼 링크) <InfoTooltip text={`${userData?.businessName || userData?.business_name || userData?.name || '부동산'} 전용 QR 코드입니다. QR코드로도 고객을 초대하실 수 있어요. 당연히 마케팅 기여도에 따라 수익에 반영됩니다. 편하신 방향으로 고객을 초대해주세요.`} /></Title>
        <Desc>
          고객이 이 QR을 스캔하면 본인 부동산 전용 신청 폼으로 이동합니다.
        </Desc>

        <QrArea>
          {qrDataUrl ? <QrImg src={qrDataUrl} alt="QR 코드" /> : <span>생성 중...</span>}
        </QrArea>

        <QrUrl>{formUrl}</QrUrl>

        <BtnRow>
          <OutlineBtn onClick={handleShare}>링크 공유하기</OutlineBtn>
          <FilledBtn onClick={handleDownload}>QR코드 발급 (저장)</FilledBtn>
        </BtnRow>
      </Card>
    </Page>
  );
};

export default QrCode;
